import React, { ReactElement, useRef } from 'react'
import cx from 'classnames'
import * as styles from '../../styles/components/content-blocks/image-grid.module.scss'
import reactElementToString from '../../utils/react-element-to-string'
import CaptionText from '../caption-text'
import ContentWrapper from '../content-wrapper'
import SmallTitle from '../small-title'
import { motion, useScroll, useTransform } from 'framer-motion'
import { bezierEaseIn } from '../../utils/styles'

export interface Image {
    image?: string
    title?: string
    body?: ReactElement | string
    altText: string
}

export interface ImageGridBlockProps {
    images: Image[]
}

const ImageGridItem = ({ img, className }: {img: Image, className?: string}) => {
    const targetRef = useRef<HTMLDivElement>(null)
    const { scrollYProgress } = useScroll({ target: targetRef, offset: ['start end', 'start start'] })
    const opacity = useTransform(scrollYProgress, [0, 0.7], [0, 1], { ease: bezierEaseIn })

    return (
        <motion.div className={className} ref={targetRef} style={{ opacity }}>
            {img.image &&
                <div className={styles.imageContainer}>
                    <img
                        src={img.image}
                        alt={img.altText}
                        className={styles.image}
                    />
                </div>
            }
            {(img.title || img.body) &&
                <div className={styles.descriptionContainer}>
                    {img.title &&
                        <div className={styles.title}>
                            <SmallTitle>{img.title}</SmallTitle>
                        </div>
                    }
                    {img.body &&
                        <CaptionText
                            className={styles.body}
                            dangerouslySetInnerHTML={{ __html: reactElementToString(img.body) }}
                        />
                    }
                </div>
            }
        </motion.div>
    )
}

const ImageGridBlock = ({ images }: ImageGridBlockProps) => {
    const itemsCount = images.length
    const imagesOnly = images.every((img) => img.image && !img.title && !img.body)

    return (
        <>
            <ContentWrapper
                className={styles.container}
            >
                <div
                    className={cx(styles.grid, styles[`items${itemsCount}`], {
                        [styles.imagesOnly]: imagesOnly,
                    })}
                >

                    {images.map((image, index) =>
                        <ImageGridItem
                            img={image}
                            className={cx(styles[`item${index + 1}`])}
                            key={`${image.altText} ${index}`}
                        />,
                    )}
                </div>
            </ContentWrapper>
        </>
    )
}

export default ImageGridBlock
